<template>
  <!-- 🟦 обгортка на всю висоту колонки -->
  <div class="h-full flex flex-col">
    <!-- 🟦 -->

    <div class="mb-4 flex items-center justify-between">
      <h2 class="text-lg font-semibold">Мої замовлення</h2>
      <button
        v-if="!loading && orders.length && !noMore"
        class="text-sm underline hover:opacity-80"
        :disabled="moreLoading"
        @click="fetchMore"
      >
        {{ moreLoading ? "Завантажуємо…" : "Показати ще" }}
      </button>
    </div>

    <!-- 🟦 скролюваний контейнер під заголовком -->
    <div class="min-h-0 flex-1 overflow-auto pr-1 space-y-3">
      <!-- 🟦 -->

      <!-- Скелетони при першому завантаженні -->
      <div v-if="loading && !orders.length" class="space-y-3">
        <div
          v-for="i in 3"
          :key="i"
          class="h-16 bg-gray-100 animate-pulse rounded-xl"
        ></div>
      </div>

      <!-- Список замовлень -->
      <ul v-else-if="orders.length" class="space-y-3">
        <li
          v-for="o in orders"
          :key="o.id"
          class="border rounded-xl p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="min-w-0">
            <div class="text-sm text-gray-500">№ {{ o.number || o.id }}</div>
            <div class="text-sm">
              <span class="font-medium">{{
                formatPrice(o.total, o.currency)
              }}</span>
              · {{ formatDate(o.createdAt) }}
            </div>
            <div class="text-xs text-gray-500 flex items-center gap-2">
              <span>{{ o.items?.length || 0 }} товар(и)</span>
              <AdminOrderStatusBadge :status="o.status" />
            </div>
            <div class="text-xs text-gray-600 mt-1">
              <span v-if="o.paymentMethod === 'card'">
                Оплата:
                {{
                  o.status === "paid" || o.payment?.status === "success"
                    ? "оплачено"
                    : "очікується"
                }}
              </span>
              <span v-else-if="String(o.payment?.mode || '') === 'cod_prepay'">
                Передоплата
                {{ Number(o.payment?.prepayAmount || o.codPrepayAmount || 0) }}
                грн:
                {{
                  String(o.payment?.status || "") === "success"
                    ? "оплачено"
                    : "очікується"
                }}
              </span>
              <span v-else>Оплата: при отриманні</span>
            </div>
          </div>

          <div
            class="shrink-0 w-full sm:w-auto flex flex-col items-stretch sm:items-end gap-1"
          >
            <p
              v-if="actionErrorId === o.id && actionError"
              class="text-xs text-red-600 text-right max-w-[16rem]"
            >
              {{ actionError }}
            </p>

            <div
              class="w-full sm:w-auto flex flex-col sm:flex-row flex-wrap sm:justify-end gap-2"
            >
              <button
                v-if="canPay(o)"
                class="w-full sm:w-auto px-3 py-1.5 rounded-lg bg-pink-200 text-black hover:bg-pink-200/80 text-sm disabled:opacity-60"
                :disabled="!!payId || !!syncId || !!cancelId"
                @click="payNow(o.id)"
              >
                {{ payId === o.id ? "Переходимо…" : "Оплатити" }}
              </button>

              <button
                v-if="canPay(o)"
                class="w-full sm:w-auto px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-sm disabled:opacity-60"
                :disabled="!!payId || !!syncId || !!cancelId"
                @click="syncStatus(o.id)"
              >
                {{ syncId === o.id ? "Перевіряємо…" : "Оновити" }}
              </button>

              <button
                v-if="canPay(o)"
                class="w-full sm:w-auto px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm disabled:opacity-60"
                :disabled="!!payId || !!syncId || !!cancelId"
                @click="cancelNow(o)"
              >
                {{ cancelId === o.id ? "Скасовуємо…" : "Скасувати" }}
              </button>

              <NuxtLink
                :to="`/order/${o.id}`"
                class="w-full sm:w-auto px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-sm text-center"
              >
                Деталі
              </NuxtLink>
            </div>
          </div>
        </li>
      </ul>

      <!-- Порожній стан -->
      <div v-else class="text-gray-500 text-sm">
        У вас поки немає замовлень.
      </div>

      <!-- Помилка -->
      <p v-if="error" class="text-sm text-red-600">{{ error }}</p>
    </div>
    <!-- 🟦 кінець скрол-контейнера -->
  </div>
</template>

<script setup>
// 🔵 самостійний блок підвантаження замовлень
import { ref, onMounted, watch, computed, onBeforeUnmount } from "vue";
import { useNuxtApp } from "#app";
import { useAuthStore } from "~/stores/auth";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import AdminOrderStatusBadge from "~/components/admin/orders/AdminOrderStatusBadge.vue";
import { useConfirm } from "~/composables/useConfirm";
import { formatPrice } from "~/utils/money";

// Стан
const orders = ref([]);
const loading = ref(false); // перше завантаження
const moreLoading = ref(false); // дозавантаження
const error = ref("");
const lastDoc = ref(null);
const noMore = ref(false);

// 🔵 payment actions state (per order)
const payId = ref("");
const syncId = ref("");
const cancelId = ref("");
const actionErrorId = ref("");
const actionError = ref("");

const { confirm } = useConfirm();

// 🔵 auto-sync pending card orders (light polling)
let autoSyncTimer = null;
const autoSyncRunning = ref(false);
const autoSyncTicks = ref(0);

// schedule: after 1s, after 3s, then every 5s
const AUTO_SYNC_PROFILE_MAX_TICKS = 50; // ~4 minutes total with 1s/3s/5s cadence
function nextProfileAutoSyncDelayMs(tickIndex) {
  if (tickIndex === 0) return 1000;
  if (tickIndex === 1) return 3000;
  return 5000;
}

const hasPendingCardOrders = computed(() =>
  (orders.value || []).some(
    (o) =>
      (o?.paymentMethod === "card" ||
        String(o?.payment?.mode || "") === "cod_prepay") &&
      o?.status === "pending_payment" &&
      o?.payment?.invoiceId,
  ),
);

// Auto-start/stop profile auto-sync if pending card orders appear/disappear
watch(
  hasPendingCardOrders,
  (has) => {
    if (has) scheduleProfileAutoSync();
    else stopProfileAutoSync();
  },
  { immediate: true },
);

function stopProfileAutoSync() {
  if (autoSyncTimer) {
    clearTimeout(autoSyncTimer);
    autoSyncTimer = null;
  }
  autoSyncRunning.value = false;
  autoSyncTicks.value = 0;
}

async function autoSyncOnce() {
  if (autoSyncRunning.value) return;
  if (!hasPendingCardOrders.value) return;

  // pause when tab hidden
  if (typeof document !== "undefined" && document.visibilityState === "hidden")
    return;

  // don't fight with manual actions
  if (payId.value || syncId.value) return;

  const target = (orders.value || []).find(
    (o) =>
      (o?.paymentMethod === "card" ||
        String(o?.payment?.mode || "") === "cod_prepay") &&
      o?.status === "pending_payment" &&
      o?.payment?.invoiceId,
  );

  if (!target) return;

  autoSyncRunning.value = true;
  try {
    const fn = httpsCallable($functions, "syncCardPaymentStatus");
    await fn({ orderId: String(target.id) });
    await fetchFirst(); // refresh list
  } catch {
    // ignore in auto mode
  } finally {
    autoSyncRunning.value = false;
  }
}

function scheduleProfileAutoSync() {
  if (autoSyncTimer) return;

  const delay = nextProfileAutoSyncDelayMs(autoSyncTicks.value);

  autoSyncTimer = setTimeout(async () => {
    autoSyncTimer = null;

    if (!hasPendingCardOrders.value) {
      stopProfileAutoSync();
      return;
    }

    if (autoSyncTicks.value >= AUTO_SYNC_PROFILE_MAX_TICKS) {
      stopProfileAutoSync();
      return;
    }

    autoSyncTicks.value += 1;
    await autoSyncOnce();

    scheduleProfileAutoSync();
  }, delay);
}

function canPay(o) {
  if (!o) return false;

  const mode = String(o?.payment?.mode || "");
  const isCard = o.paymentMethod === "card";
  const isCodPrepay = mode === "cod_prepay";

  const isPending = o.status === "pending_payment";
  const payStatus = String(o?.payment?.status || "");
  const alreadyPaid = payStatus === "success" || o.status === "paid";

  return (isCard || isCodPrepay) && isPending && !alreadyPaid;
}

function humanizePaymentError(e) {
  const code = String(e?.code || "");
  const msg = String(e?.message || "");

  if (code === "unauthenticated") return "Увійдіть в акаунт, щоб оплатити.";
  if (code === "permission-denied") return "Немає доступу до цього замовлення.";
  if (code === "unavailable")
    return "Сервіс тимчасово недоступний. Спробуйте пізніше.";

  if (msg.includes("payment-invoice-create-failed")) {
    return "Не вдалося створити рахунок на оплату. Спробуйте пізніше.";
  }
  if (msg.includes("payment-status-unavailable")) {
    return "Не вдалося перевірити статус оплати. Спробуйте пізніше.";
  }
  if (msg.includes("order-payment-in-progress")) {
    return "Оплата вже розпочата або пройшла — скасувати не можна. Натисніть «Оновити».";
  }
  if (msg.includes("order-already-canceled")) {
    return "Замовлення вже скасоване.";
  }
  if (msg.includes("order-not-cancelable")) {
    return "Це замовлення вже не можна скасувати.";
  }
  if (msg.includes("cancel-order-failed")) {
    return "Не вдалося скасувати замовлення. Спробуйте ще раз.";
  }

  return msg || "Сталася помилка. Спробуйте ще раз.";
}

async function cancelNow(o) {
  const orderId = String(o?.id || "");
  if (!orderId || cancelId.value) return;

  const ok = await confirm({
    title: "Скасувати замовлення?",
    message: `Замовлення № ${o.number || orderId} буде скасовано. Якщо у вас була знижка 10% на перше замовлення — вона повернеться.`,
    confirmText: "Скасувати замовлення",
    cancelText: "Залишити",
  });
  if (!ok) return;

  actionError.value = "";
  actionErrorId.value = "";
  cancelId.value = orderId;

  try {
    const fn = httpsCallable($functions, "cancelOrder");
    await fn({ orderId });
    await fetchFirst(); // одразу показати статус "Скасовано"
  } catch (e) {
    actionError.value = humanizePaymentError(e);
    actionErrorId.value = orderId;
  } finally {
    cancelId.value = "";
  }
}

async function payNow(orderId) {
  actionError.value = "";
  actionErrorId.value = "";
  payId.value = String(orderId || "");

  try {
    const fn = httpsCallable($functions, "createCardPaymentInvoice");
    const { data } = await fn({ orderId: payId.value });
    const pageUrl = data?.pageUrl;

    if (data?.alreadyPaid) {
      // бекенд вже підтвердив оплату - просто оновимо список
      await fetchFirst();
      return;
    }

    if (!pageUrl) throw new Error("Не вдалося отримати посилання на оплату.");

    // mark that we initiated payment so on return we can sync immediately on /order/:id
    try {
      const key = `mb_pay_return_${String(orderId)}`;
      window.localStorage.setItem(key, "1");
    } catch {}

    window.location.href = pageUrl;
  } catch (e) {
    actionError.value = humanizePaymentError(e);
    actionErrorId.value = payId.value;
  } finally {
    payId.value = "";
  }
}

async function syncStatus(orderId) {
  actionError.value = "";
  actionErrorId.value = "";
  syncId.value = String(orderId || "");

  try {
    const fn = httpsCallable($functions, "syncCardPaymentStatus");
    await fn({ orderId: syncId.value });
    await fetchFirst(); // щоб одразу побачити paid
  } catch (e) {
    actionError.value = humanizePaymentError(e);
    actionErrorId.value = syncId.value;
  } finally {
    syncId.value = "";
  }
}

const PAGE = 10;

const auth = useAuthStore();
const { $db, $functions } = useNuxtApp();

function mapDoc(d) {
  const data = d.data ? d.data() : d;
  return {
    id: d.id,
    ...data,
  };
}

async function fetchFirst() {
  if (!$db || !auth.user) return;
  loading.value = true;
  error.value = "";
  noMore.value = false;
  lastDoc.value = null;
  try {
    const q = query(
      collection($db, "orders"),
      where("userId", "==", auth.user.uid),
      orderBy("createdAt", "desc"),
      limit(PAGE),
    );
    const snap = await getDocs(q);
    orders.value = snap.docs.map(mapDoc);
    lastDoc.value = snap.docs[snap.docs.length - 1] || null;
    noMore.value = snap.docs.length < PAGE;
  } catch (e) {
    error.value = e?.message || String(e);
  } finally {
    loading.value = false;
  }
}

async function fetchMore() {
  if (noMore.value || moreLoading.value || !lastDoc.value) return;
  moreLoading.value = true;
  error.value = "";
  try {
    const q = query(
      collection($db, "orders"),
      where("userId", "==", auth.user.uid),
      orderBy("createdAt", "desc"),
      startAfter(lastDoc.value),
      limit(PAGE),
    );
    const snap = await getDocs(q);
    const items = snap.docs.map(mapDoc);
    orders.value.push(...items);
    lastDoc.value = snap.docs[snap.docs.length - 1] || lastDoc.value;
    if (snap.docs.length < PAGE) noMore.value = true;
  } catch (e) {
    error.value = e?.message || String(e);
  } finally {
    moreLoading.value = false;
  }
}

// Авто-підвантаження при готовності Auth
onMounted(async () => {
  if (auth.whenReady) await auth.whenReady();
  if (auth.user) await fetchFirst();
});

// Якщо користувач змінився — перезавантажити список
watch(
  () => auth.user?.uid,
  async (uid, prev) => {
    if (!uid) {
      orders.value = [];
      noMore.value = false;
      lastDoc.value = null;
      stopProfileAutoSync();
      return;
    }
    if (uid !== prev) await fetchFirst();
  },
);

// Хелпери форматування
function formatDate(ts) {
  try {
    const d = ts?.toDate
      ? ts.toDate()
      : typeof ts === "number"
        ? new Date(ts)
        : new Date(ts);
    return d.toLocaleDateString("uk-UA", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return "";
  }
}
onBeforeUnmount(() => {
  stopProfileAutoSync();
});
</script>

<style scoped></style>
